// @ts-strict-ignore
import React from 'react';
import type {
  ComponentProps,
  ComponentType,
  CSSProperties,
  ReactNode,
  SVGProps,
} from 'react';

import { Block } from '@actual-app/components/block';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import { DropHighlight, useDroppable } from '#components/sort';
import type { OnDropCallback } from '#components/sort';
import { CellValue, CellValueText } from '#components/spreadsheet/CellValue';
import type { Binding, SheetFields } from '#spreadsheet';

import { accountNameStyle } from './Account';
import { ItemContent } from './ItemContent';

const fontWeight = 600;

type SecondaryItemProps = {
  title: string;
  to?: string;
  Icon?:
    | ComponentType<SVGProps<SVGElement>>
    | ComponentType<SVGProps<SVGSVGElement>>;
  style?: CSSProperties;
  onClick?: ComponentProps<typeof ItemContent>['onClick'];
  bold?: boolean;
  indent?: number;
  rightContent?: ReactNode;
  actions?: ReactNode;
  dropId?: string;
  dropTypes?: string | string[];
  onDrop?: OnDropCallback;
  color?: string;
  titleStyle?: CSSProperties;
  ariaLabel?: string;
  balance?: Binding<'account', SheetFields<'account'>>;
  testId?: string;
};

export function SecondaryItem({
  Icon,
  title,
  style,
  to,
  onClick,
  bold,
  indent = 0,
  rightContent,
  actions,
  dropId,
  dropTypes,
  onDrop,
  color,
  titleStyle,
  ariaLabel,
  balance,
  testId,
}: SecondaryItemProps) {
  const { dropRef, dropPos } = useDroppable({
    types: dropTypes ?? [],
    id: dropId ?? title,
    onDrop: onDrop ?? (() => undefined),
  });
  const content = (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        height: 16,
      }}
    >
      {Icon && <Icon width={12} height={12} />}
      <Block
        style={{
          marginLeft: Icon ? 8 : 0,
          color: color ?? 'inherit',
          flex: 1,
          ...titleStyle,
        }}
      >
        {title}
      </Block>
      {rightContent}
      {balance && (
        <CellValue binding={balance} type="financial">
          {props => <CellValueText {...props} value={Number(props.value)} />}
        </CellValue>
      )}
    </View>
  );

  return (
    <View
      data-testid={testId}
      style={{ flexShrink: 0, flexDirection: 'row', ...style }}
    >
      <View style={{ flex: 1 }} innerRef={dropId ? dropRef : undefined}>
        {dropId && <DropHighlight pos={dropPos} />}
        <ItemContent
          style={{
            ...accountNameStyle,
            color: theme.sidebarItemText,
            paddingLeft: 14 + indent,
            fontWeight: bold ? fontWeight : null,
            ':hover': { backgroundColor: theme.sidebarItemBackgroundHover },
          }}
          to={to}
          onClick={onClick}
          ariaLabel={ariaLabel}
          activeStyle={{
            borderLeft: '4px solid ' + theme.sidebarItemTextSelected,
            paddingLeft: 14 - 4 + indent,
            color: theme.sidebarItemTextSelected,
            fontWeight: bold ? fontWeight : null,
          }}
        >
          {content}
        </ItemContent>
      </View>
      {actions}
    </View>
  );
}
